/* eslint-disable react-hooks/exhaustive-deps */
import { IoChevronDown, IoChevronUp, IoCopy, IoCreateOutline, IoSaveOutline, IoCloseOutline, IoCopyOutline } from "react-icons/io5"
import { FaClock, FaTruck, FaMapMarkerAlt, FaBuilding, FaBox } from "react-icons/fa"
import { Key, useState, useEffect } from "react";
import useSearchMiddlewareOSRM from "hooks/useSearchMiddlewareOSRM"
import useCombinedScheduleBFF from "hooks/bff_entities/useCombinedScheduleBFF"
import { CustomSearchSelect } from './CustomSearchSelect'
import cn from "classnames"
import useCoveragesResources from "hooks/entities/useCoveragesResources"
import { formatLocationName } from "../../../utils/utils"

interface CoverageCardProps {
  coverage: any;
  cardId: string;
  isExpanded: boolean;
  isSelected: boolean;
  onToggleExpansion: (cardId: string) => void;
  onSelect: (cardId: string) => void;
  onCopyToClipboard: (text: string) => void;
  onSave?: (cardId: string, updatedCoverage: any) => void;
  onDuplicate?: (cardId: string, coverage: any) => void;
}

interface RouteData {
  id: string;
  source: any;
  target: any;
}

interface CourierData {
  id: string;
  name: string;
  alias?: string;
  channel?: string[];
  shippingType?: string[];
  deliveryType?: string;
}

export const CoverageCard: React.FC<CoverageCardProps> = ({
  coverage,
  cardId,
  isExpanded,
  isSelected,
  onToggleExpansion,
  onSelect,
  onCopyToClipboard,
  onSave,
  onDuplicate,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedCoverage, setEditedCoverage] = useState(coverage);

  // Hooks for data fetching
  const { data: routesData, search_osrm_action: searchRoutes } = useSearchMiddlewareOSRM({ entity: "routes" })
  const { data: couriersData, search_osrm_action: searchCouriers } = useSearchMiddlewareOSRM({ entity: "couriers" })
  const { get_schedule_types_action, set_loading_app } = useCombinedScheduleBFF()
  const { put_upsert_coverage_action, set_loading_app: set_loading_app_upsert_coverage} = useCoveragesResources()

  // Search queries
  const [routeQuery, setRouteQuery] = useState("")
  const [courierQuery, setCourierQuery] = useState("")
  const isDuplicated = coverage.tempId && !coverage.id;

  // Schedule types
  const [deliveryTypes, setDeliveryTypes] = useState<string[]>([])
  const [shippingTypes, setShippingTypes] = useState<string[]>([])

  // Data arrays
  const routes: RouteData[] = routesData?.records || []
  const couriers: CourierData[] = couriersData?.records || []

  useEffect(() => {
    // Filter routes based on source and target when in edit mode
    if (isEditMode) {
      searchRoutes({ from: 0, size: 500, queryString: routeQuery })
    }
  }, [routeQuery, isEditMode])

  useEffect(() => {
    // Filter couriers based on name and channel when in edit mode
    if (isEditMode) {
      searchCouriers({ from: 0, size: 500, queryString: courierQuery })
    }
  }, [courierQuery, isEditMode])

  useEffect(() => {
    if (isDuplicated) {
      setIsEditMode(true);
    }
  }, [isDuplicated]);

  useEffect(() => {
    const fetchScheduleTypes = async () => {
      if (isEditMode) {
        try {
          set_loading_app(true)
          const response = await get_schedule_types_action("")
          
          if (response) {
            setDeliveryTypes(response.deliveryTypes || [])
            setShippingTypes(response.shippingTypes || [])
          }
        } catch (error) {
          console.error("Error fetching schedule types:", error)
        } finally {
          set_loading_app(false)
        }
      }
    }
    
    fetchScheduleTypes()
  }, [isEditMode])

  const getDayName = (dayNum: number) => {
    const days = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
    return days[dayNum] || "";
  };

  const formatWorkingDays = (days: number[]) => {
    if (!days || days.length === 0) return "No especificado";
    return days.map(day => getDayName(day-1)).join(", ");
  };

  const formatTimeForInput = (time: string) => {
    if (!time) return "";
    // If time is in H:MM format, convert to HH:MM
    const parts = time.split(':');
    if (parts.length === 2) {
      const hours = parts[0].padStart(2, '0');
      const minutes = parts[1];
      return `${hours}:${minutes}`;
    }
    return time;
  };

  const formatWorkingHours = (time: any) => {
    return `${time.from} - ${time.to}`
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditMode(true);
    setEditedCoverage({ ...coverage });
  };

 const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      set_loading_app_upsert_coverage(true);
      const copyCoverage = { ...editedCoverage };
      if(copyCoverage.tempId && isDuplicated){
        copyCoverage.id = copyCoverage.routeId + '_' + copyCoverage.courierId + '_'+copyCoverage.deliveryType;
      }
      delete copyCoverage.route;
      delete copyCoverage.courier;
      delete copyCoverage.tempId;
      const response = await put_upsert_coverage_action({ 
        body: copyCoverage 
      });
      
      if (response) {
        onSave?.(cardId, editedCoverage);
        setIsEditMode(false);
      }
    } catch (error) {
      console.error("Error saving coverage:", error);
      // Error handling is already done in the hook via SweetAlert
    } finally {
      set_loading_app_upsert_coverage(false);
    }
  };

const handleCancel = (e: React.MouseEvent) => {
  e.stopPropagation();
  
  if (isDuplicated) {
    // For duplicated coverage, call onSave with null to indicate deletion
    onSave?.(coverage.tempId  , null);
  } else {
    // For normal coverage, just reset the form
    setEditedCoverage(coverage);
    setIsEditMode(false);
  }
};

  const handleInputChange = (path: string, value: any) => {
    const keys = path.split('.');
    const newCoverage = { ...editedCoverage };
    let current = newCoverage;
    
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    
    setEditedCoverage(newCoverage);
  };

  const handleRouteSelect = (routeId: string) => {
    const selectedRoute = routes.find(r => r.id === routeId);
    if (selectedRoute) {
      const newCoverage = { ...editedCoverage };
      newCoverage.routeId = routeId;
      newCoverage.route = selectedRoute;
      setEditedCoverage(newCoverage);
    }
  };

  const handleCourierSelect = (courierId: string) => {
    const selectedCourier = couriers.find(c => c.id === courierId);
    if (selectedCourier) {
      const newCoverage = { ...editedCoverage };
      newCoverage.courierId = courierId;
      newCoverage.courier = selectedCourier;
      setEditedCoverage(newCoverage);
    }
  };

  const currentCoverage = isEditMode ? editedCoverage : coverage;

  return (
    <div className={`bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow duration-200 w-full ${
      isDuplicated ? 'border-purple-300 bg-purple-50' : 'border-gray-200'
    }`}>
      {/* Card Header - Always visible */}
      <div 
        className="bg-gray-50 p-4 border-b border-gray-200 cursor-pointer"
        onClick={() => !isEditMode && onToggleExpansion(cardId)}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div onClick={(e) => e.stopPropagation()}>
              {
                !isEditMode ? 
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onSelect(cardId)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    disabled={isEditMode}
                  /> 
                : null 
              }
            </div>
            {
              isEditMode ? (
                <label className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleInputChange('status', !currentCoverage.status)}
                  className={cn(
                  "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none",
                  currentCoverage.status ? "bg-green-500" : "bg-gray-300"
                  )}
                  aria-pressed={!!currentCoverage.status}
                >
                  <span
                  className={cn(
                    "inline-block h-5 w-5 transform rounded-full bg-white transition-transform",
                    currentCoverage.status ? "translate-x-5" : "translate-x-1"
                  )}
                  />
                </button>
                </label>
              ) : <span className={`w-3 h-3 rounded-full ${coverage.status ? 'bg-green-500' : 'bg-red-500'}`}></span>
            }
            
            <h3 className="font-medium text-gray-800">
                {(  currentCoverage.route.source.type === "Source" ?
                      currentCoverage.route.source.data.name :
                      currentCoverage.route.source.type === "Location" ?
                        formatLocationName(currentCoverage.route.source.data) : ''
                )} → 
                {' ' + ( currentCoverage.route.target.type === "Location" ? 
                          formatLocationName(currentCoverage.route.target.data) : 
                          (currentCoverage.route.target.type  === "Source" ?
                              currentCoverage.route.target.data.name 
                              : '')
                      )
                }
            </h3>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex flex-col">
              <span className="text-sm text-gray-600">{currentCoverage.courier.name || "Sin courier"}</span>
              <span className="text-[10px] text-gray-500/80">{currentCoverage.courier.id || ""}</span>
            </div>
            
            
            {/* Edit/Save/Cancel buttons */}
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {isEditMode ? (
                <>
                  <button
                    onClick={handleSave}
                    className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                    title="Guardar"
                  >
                    <IoSaveOutline size={16} />
                  </button>
                  <button
                    onClick={handleCancel}
                    className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                    title="Cancelar"
                  >
                    <IoCloseOutline size={16} />
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleEdit}
                    className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                    title="Editar"
                  >
                    <IoCreateOutline size={16} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicate?.(cardId, coverage);
                    }}
                    className="p-1.5 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded transition-colors"
                    title="Duplicar"
                  >
                    <IoCopyOutline size={16} />
                  </button>
                </>
              )}
            </div>

            
            
            {!isEditMode && (
              <>
                {isExpanded ? <IoChevronUp className="text-gray-500" /> : <IoChevronDown className="text-gray-500" />}
              </>
            )}
          </div>
        </div>
        
        {/* Summary info when collapsed */}
        {!isExpanded && !isEditMode && (
          <div className="mt-2 flex flex-wrap gap-3 text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <FaClock className="text-purple-600" />
              <span>{currentCoverage.leadtime.value} {currentCoverage.leadtime.unit === 'd' ? 'día(s)' : 'hora(s)'}</span>
            </div>
            <div className="flex items-center gap-1">
              <FaBox className="text-orange-600" />
              <span>
                {Array.isArray(currentCoverage.shippingType) 
                  ? currentCoverage.shippingType.join(", ") 
                  : currentCoverage.shippingType || "No especificado"}
              </span>
            </div>
            <div className="flex items-center gap-1">
            <FaTruck className="text-green-600" />
              <span>{currentCoverage.deliveryType || "No especificado"}</span>
            </div>
          </div>
        )}
      </div>
        
      {/* Card Body - Only visible when expanded or in edit mode */}
      {(isExpanded || isEditMode) && (
        <div className="p-4 space-y-4">
          {/* Route Selection in Edit Mode */}
          {isEditMode && (
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Seleccionar Nueva Ruta ({`${currentCoverage.route.source?.data?.name || ( formatLocationName(currentCoverage.route.source.data) ) || 'Origen'} 
                  → ${currentCoverage.route.target?.data?.name || ( formatLocationName(currentCoverage.route.target.data) ) || 'Destino'}`})</h4>
              <CustomSearchSelect
                value={""}
                onChange={handleRouteSelect}
                options={routes.map(route => ({
                  value: route.id,
                  label: `${route.source?.data?.name || ( formatLocationName(route.source.data) ) || 'Origen'} 
                  → ${route.target?.data?.name || ( formatLocationName(route.target.data) ) || 'Destino'}`
                }))}
                placeholder="Buscar y seleccionar ruta"
                onSearch={setRouteQuery}
              />
            </div>
          )}

          {/* Courier Selection in Edit Mode */}
          {isEditMode && (
            <div className="border border-green-200 rounded-lg p-4 bg-green-50">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Seleccionar Nuevo Courier ({currentCoverage.courier.name}-{currentCoverage.courier.id})</h4>
              <CustomSearchSelect
                value={""}
                onChange={handleCourierSelect}
                options={couriers.map(courier => ({
                  value: courier.id,
                  label: `${courier.name} ${courier.alias ? `(ALIAS: ${courier.alias} ID: ${courier.id})` : ''}`
                }))}
                placeholder="Buscar y seleccionar courier"
                onSearch={setCourierQuery}
              />
            </div>
          )}

         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Route Information */}
            {!isEditMode && (
              <div>
                <div className="flex justify-between items-center">
                <h4 className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <FaMapMarkerAlt className="text-blue-600" /> Información de Ruta
                  </h4>
                  {!isEditMode && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopyToClipboard(currentCoverage.routeId);
                      }}
                      className="text-gray-500 hover:text-gray-700 p-1"
                      title="Copiar ID de Ruta"
                    >
                      <IoCopy size={16} />
                    </button>
                  )}
                </div>
                <div className="bg-blue-50 p-3 rounded-md">
                  <div className="flex items-start gap-2 mb-2">
                    <FaBuilding className="text-blue-600 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">Origen ({currentCoverage.route.source.type})</p>
                      {currentCoverage.route.source.type === "Source" ? (
                        <p className="text-sm font-medium">{currentCoverage.route.source.data.name} ({currentCoverage.route.source.data.id})</p>
                      ) : (
                        <p className="text-sm font-medium"> { formatLocationName(currentCoverage.route.source.data) } </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <FaMapMarkerAlt className="text-blue-600 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">Destino ({currentCoverage.route.target.type})</p>
                      {currentCoverage.route.target.type === "Source" ? (
                        <p className="text-sm font-medium">{currentCoverage.route.target.data.name} ({currentCoverage.route.target.data.id})</p>
                      ) : (
                        <p className="text-sm font-medium">{ formatLocationName(currentCoverage.route.target.data) } </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Courier Information */}
            {!isEditMode && (
              <div>
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <FaTruck className="text-green-600" /> Información de Courier
                  </h4>
                  {!isEditMode && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopyToClipboard(currentCoverage.courierId);
                      }}
                      className="text-gray-500 hover:text-gray-700 p-1"
                      title="Copiar ID de Courier"
                    >
                      <IoCopy size={16} />
                    </button>
                  )}
                </div>
                <div className="bg-green-50 p-3 rounded-md">
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <p className="text-xs text-gray-500">Nombre</p>
                      <p className="text-sm font-medium">{currentCoverage.courier.name || "No especificado"}</p>
                    </div>                  
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <p className="text-xs text-gray-500">Alias</p>
                      <p className="text-sm font-medium">{currentCoverage.courier.alias || "No especificado"}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <p className="text-xs text-gray-500">Canal</p>
                      <div className="flex flex-col gap-1">
                        {Array.isArray(currentCoverage.courier?.channel) ? (
                        currentCoverage.courier.channel.map((channel: string) => (
                          <span key={channel} className="text-sm font-medium">
                          {channel}
                          </span>
                        ))
                        ) : (
                        <span className="text-sm font-medium">No especificado</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Tipos de Envío</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                          {Array.isArray(currentCoverage.courier?.shippingType) ? (
                              currentCoverage.courier.shippingType.map((type: string) => (
                              <span key={type} className="bg-orange-100 text-orange-800 px-2 py-0.5 rounded text-xs">
                                  {type}
                              </span>
                              ))
                          ) : (
                              <span className="text-sm font-medium">No especificado</span>
                          )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Tipo de Entrega</p>
                        <p className="text-sm font-medium">{currentCoverage.courier.deliveryType || "No especificado"}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Shipping and Delivery Types */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-1">
                <FaBox className="text-orange-600" /> Tipos de Envío y Entrega
              </h4>
              <div className="bg-orange-50 p-3 rounded-md">
                <p className="text-xs text-gray-500">Tipos de Envío</p>
                {isEditMode ? (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {shippingTypes.map(type => {
                      const isSelected = Array.isArray(currentCoverage.shippingType) && currentCoverage.shippingType.includes(type);
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => {
                            const currentTypes = Array.isArray(currentCoverage.shippingType) ? currentCoverage.shippingType : [];
                            const newTypes = isSelected
                              ? currentTypes.filter((t: string) => t !== type)
                              : [...currentTypes, type];
                            handleInputChange('shippingType', newTypes);
                          }}
                          className={`px-2 py-0.5 rounded text-xs font-medium border transition-colors ${
                            isSelected
                              ? 'bg-orange-100 text-orange-800 border-orange-200'
                              : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'
                          }`}
                        >
                          {type}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  // existing non-edit display code remains the same
                  <div className="flex flex-wrap gap-1 mt-1">
                    {Array.isArray(currentCoverage.shippingType) ? (
                      currentCoverage.shippingType.map((type: string) => (
                        <span key={type} className="bg-orange-100 text-orange-800 px-2 py-0.5 rounded text-xs">
                          {type}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm font-medium">No especificado</span>
                    )}
                  </div>
                )}
              </div>
              <div className="bg-orange-50 p-3 rounded-md">
                <p className="text-xs text-gray-500">Tipo de Entrega</p>
                {isEditMode ? (
                  <select
                    value={currentCoverage.deliveryType || ""}
                    onChange={(e) => handleInputChange('deliveryType', e.target.value)}
                    className={cn(
                      "w-full text-sm bg-white border border-gray-300 rounded px-2 py-1",
                      { "text-gray-500": currentCoverage.deliveryType === "" }
                    )}
                  >
                    <option value="" disabled hidden className="text-gray-500">Seleccionar tipo</option>
                    {deliveryTypes.map(type => (
                      <option key={type} value={type} className="text-gray-700">{type}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm font-medium">{currentCoverage.deliveryType || "No especificado"}</p>
                )}
              </div>
            </div>

            {/* Leadtime and Schedule */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-1">
                <FaClock className="text-purple-600" /> Tiempos de Entrega
              </h4>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-purple-50 p-3 rounded-md">
                  <p className="text-xs text-gray-500">Tiempo de entrega</p>
                  {isEditMode ? (
                    <div className="flex flex-col gap-2 mt-1">
                      <input
                        type="number"
                        value={currentCoverage.leadtime.value || ""}
                        onChange={(e) => handleInputChange('leadtime.value', parseInt(e.target.value) || 0)}
                        className="flex-1 text-sm font-medium bg-white border border-gray-300 rounded px-2 py-1"
                        min="0"
                      />
                      <select
                        value={currentCoverage.leadtime.unit || "d"}
                        onChange={(e) => handleInputChange('leadtime.unit', e.target.value)}
                        className="text-sm font-medium bg-white border border-gray-300 rounded px-2 py-1"
                      >
                        <option value="d">día(s)</option>
                        <option value="h">hora(s)</option>
                        <option value="m">minuto(s)</option>
                      </select>
                    </div>
                  ) : (
                    <p className="text-sm font-medium">
                      {currentCoverage.leadtime.value} {currentCoverage.leadtime.unit === 'd' ? 'día(s)' : currentCoverage.leadtime.unit === 'h' ? 'hora(s)' : 'minuto(s)'}
                    </p>
                  )}
                </div>
                <div className="bg-purple-50 p-3 rounded-md">
                  <p className="text-xs text-gray-500">Días de entrega</p>
                  {isEditMode ? (
                    <div className="mt-1">
                      <div className="grid grid-cols-1 gap-1 text-xs">
                        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((day, index) => (
                          <label key={index+1} className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={currentCoverage.deliveryDays?.includes(index+1) || false}
                              onChange={(e) => {
                                const currentDays = currentCoverage.deliveryDays || [];
                                const newDays = e.target.checked
                                  ? [...currentDays, index+1]
                                  : currentDays.filter((d: number) => d !== index+1);
                                handleInputChange('deliveryDays', newDays.sort());
                              }}
                              className="w-3 h-3"
                            />
                            <span>{day}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm font-medium">
                      {formatWorkingDays(currentCoverage.deliveryDays)}
                    </p>
                  )}
                </div>
                <div className="bg-purple-50 p-3 rounded-md">
                  <p className="text-xs text-gray-500">Dias no laborables</p>
                  {isEditMode ? (
                    <div className="mt-1">
                      <div className="grid grid-cols-1 gap-1 text-xs">
                        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((day, index) => (
                          <label key={index+1} className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={currentCoverage.nonWorkingDays?.includes(index+1) || false}
                              onChange={(e) => {
                                const currentDays = currentCoverage.nonWorkingDays  || [];
                                const newDays = e.target.checked
                                  ? [...currentDays, index+1]
                                  : currentDays.filter((d: number) => d !== index+1);
                                handleInputChange('nonWorkingDays', newDays.sort());
                              }}
                              className="w-3 h-3"
                            />
                            <span>{day}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm font-medium">
                      {formatWorkingDays(currentCoverage.nonWorkingDays )}
                    </p>
                  )}
                </div>
              </div>
              <div className="bg-purple-50 p-3 rounded-md">
              <p className="text-xs text-gray-500">Horario de trabajo</p>
                {isEditMode ? (
                  <div className="space-y-2 mt-1">
                    {currentCoverage.workingTime.map((time: { daysOfWeek: number[]; from: string; to: string; }, index: number) => (
                      <div key={index} className="border border-gray-200 rounded p-2 bg-white">
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <div>
                            <label className="text-xs text-gray-500">Desde:</label>
                            <input
                              type="time"
                              value={formatTimeForInput(time.from) || ""}
                              onChange={(e) => {
                                const newWorkingTime = [...currentCoverage.workingTime];
                                newWorkingTime[index] = { ...time, from: e.target.value };
                                handleInputChange('workingTime', newWorkingTime);
                              }}
                              className="w-full text-xs bg-white border border-gray-300 rounded px-1 py-0.5"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Hasta:</label>
                            <input
                              type="time"
                              value={formatTimeForInput(time.to) || ""}
                              onChange={(e) => {
                                const newWorkingTime = [...currentCoverage.workingTime];
                                newWorkingTime[index] = { ...time, to: e.target.value };
                                handleInputChange('workingTime', newWorkingTime);
                              }}
                              className="w-full text-xs bg-white border border-gray-300 rounded px-1 py-0.5"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Días de la semana:</label>
                          <div className="grid grid-cols-4 gap-1 mt-1">
                            {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((day, dayIndex) => (
                              <label key={dayIndex} className="flex items-center gap-1 text-xs">
                                <input
                                  type="checkbox"
                                  checked={time.daysOfWeek?.includes(dayIndex+1) || false}
                                  onChange={(e) => {
                                    const currentDays = time.daysOfWeek || [];
                                    const newDays = e.target.checked
                                      ? [...currentDays, dayIndex+1]
                                      : currentDays.filter((d: number) => d !== dayIndex+1);
                                    const newWorkingTime = [...currentCoverage.workingTime];
                                    newWorkingTime[index] = { ...time, daysOfWeek: newDays.sort() };
                                    handleInputChange('workingTime', newWorkingTime);
                                  }}
                                  className="w-3 h-3"
                                />
                                <span>{day}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        {/* Add/Remove working time buttons */}
                        <div className="flex justify-end gap-2 mt-2">
                          {currentCoverage.workingTime.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newWorkingTime = currentCoverage.workingTime.filter((_: any, i: number) => i !== index);
                                handleInputChange('workingTime', newWorkingTime);
                              }}
                              className="text-xs text-red-600 hover:text-red-800 px-2 py-1 border border-red-300 rounded hover:bg-red-50"
                            >
                              Eliminar
                            </button>
                          )}
                          {index === currentCoverage.workingTime.length - 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newWorkingTime = [...currentCoverage.workingTime, {
                                  daysOfWeek: [],
                                  from: "09:00",
                                  to: "18:00"
                                }];
                                handleInputChange('workingTime', newWorkingTime);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 border border-blue-300 rounded hover:bg-blue-50"
                            >
                              Agregar horario
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {currentCoverage.workingTime.map((time: { daysOfWeek: number[]; from: string | undefined; to: string | undefined; }, index: Key | null | undefined) => (
                      <div key={index}>
                          <p className="text-sm font-medium">
                              {formatWorkingDays(time.daysOfWeek)}
                          </p>
                          <p className="text-sm font-medium">
                              {formatWorkingHours(time)}
                          </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>       
          </div>                    
                        
        </div>
      )}
      
      {/* Card Footer */}
      {(isExpanded || isEditMode) && (
        <div className="bg-gray-50 p-4 border-t border-gray-200 flex justify-end">
          {isEditMode && (
            <div className="flex gap-2">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors"
              >
                Guardar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}